import React, { useCallback, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { cookie } from 'utils/cookies/index';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Modal, Button } from 'react-bootstrap';
import { IconUser, IconLogout, IconBell, IconInbox } from '@tabler/icons-react';
import Avatar from 'components/general/Avatar';
import { fetchPendingApprovals } from 'api/formApi';
import './Header.scss';


const Header = (props) => {
  const { title = '' } = props;
  const navigate = useNavigate();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const notificationRef = useRef(null);

  const onClickRemove = useCallback(() => {
    cookie.clean();
    window.location.href = '/login';
  }, []);

  const {
    user: { user },
  } = useSelector((state) => state.auth);

  const isManager = (user?.role || '').toLowerCase() === 'manager';

  const loadNotifications = useCallback(async () => {
    try {
      const response = await fetchPendingApprovals();
      if (response && response.data) {
        setNotifications(response.data);
      }
    } catch (error) {
      console.error('Failed to load notifications:', error);
    }
  }, []);

  useEffect(() => {
    if (isManager) {
      loadNotifications();
      const interval = setInterval(loadNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [isManager, loadNotifications]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNotificationClick = (item) => {
    setShowNotifications(false);
    navigate(`/formList/${item.stationId}/${item.formName}/${item.formId}`);
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      hour: '2-digit',
      minute: '2-digit',
      day: 'numeric',
      month: 'short',
    });
  };

  const prettifyFormName = (name) => {
    if (!name) return '';
    return name
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <>
      <header className="navbar navbar-expand-md sticky-top d-none d-lg-flex d-print-none premium-header">
        <div className="container-xl align-items-center d-flex justify-content-between">
          <div>
            <h2 className="page-title m-0">{title}</h2>
          </div>
          <div className="navbar-nav flex-row align-items-center gap-3">
            {/* User Details */}
            <div className="d-flex align-items-center gap-2 me-2">
              <Avatar className="avatar avatar-sm" role={user?.role} name={`${user?.name} ${user?.lastName ? user?.lastName : ''}`} />
              <div className="d-none d-xl-flex flex-column align-items-start justify-content-center">
                <div className="admin_name">
                  {user?.name}
                  {user?.lastName && ' ' + user?.lastName}
                </div>
                <div className="user_role">
                  {user?.role}
                </div>
              </div>
            </div>

            {/* Notifications (Manager Only) */}
            {isManager && (
              <div className="notification-container" ref={notificationRef}>
                <button 
                  type="button" 
                  className="notification-btn"
                  onClick={() => setShowNotifications(!showNotifications)}
                  aria-label="Notifications"
                >
                  <IconBell size={20} />
                  {notifications.length > 0 && (
                    <span className="notification-badge">{notifications.length}</span>
                  )}
                </button>

                {showNotifications && (
                  <div className="notification-dropdown">
                    <div className="dropdown-header">
                      <h3>Pending Approvals</h3>
                      {notifications.length > 0 && (
                        <span className="badge bg-primary text-white">{notifications.length} forms</span>
                      )}
                    </div>
                    <div className="dropdown-body">
                      {notifications.length > 0 ? (
                        notifications.map((item) => (
                          <div 
                            key={item.formId} 
                            className="notification-item"
                            onClick={() => handleNotificationClick(item)}
                          >
                            <div className="item-icon">
                              <IconBell size={16} />
                            </div>
                            <div className="item-content">
                              <div className="item-title">
                                {prettifyFormName(item.formName)} {item.level !== 'root' ? `(${item.level.toUpperCase()})` : ''}
                              </div>
                              <div className="item-subtitle">
                                Station ID: {item.stationId} | Project: {item.projectName}
                              </div>
                              <div className="item-time">
                                {formatTime(item.updatedAt)}
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="empty-notifications">
                          <IconInbox size={32} />
                          <div>No new forms to approve</div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Direct Action Buttons */}
            <Link 
              to="/profile" 
              className="profile-btn border-0 shadow-sm"
              style={{ borderRadius: '8px', background: 'rgba(79, 172, 254, 0.08)', color: '#0ea5e9' }}
              aria-label="Profile"
            >
              <IconUser size={20} />
            </Link>

            <Button 
              variant="outline-danger" 
              className="logout-btn border-0 shadow-sm p-0"
              style={{ borderRadius: '8px', background: 'rgba(239, 68, 68, 0.08)', color: '#ef4444' }}
              onClick={() => setShowLogoutConfirm(true)}
              aria-label="Logout"
            >
              <IconLogout size={20} />
            </Button>
          </div>
        </div>
      </header>

      {/* Logout Confirmation Modal */}
      <Modal 
        show={showLogoutConfirm} 
        onHide={() => setShowLogoutConfirm(false)} 
        centered
        dialogClassName="logout-modal"
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold text-dark d-flex align-items-center gap-2">
            <IconLogout size={24} className="text-danger" />
            Confirm Logout
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="py-3">
          <p className="m-0 text-muted" style={{ fontSize: '1rem', lineHeight: '1.5' }}>
            Are you sure you want to log out of <strong>SiteSync</strong>? Any unsaved form modifications may be lost.
          </p>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0">
          <Button 
            variant="light" 
            onClick={() => setShowLogoutConfirm(false)}
            style={{ borderRadius: '8px', fontWeight: '600' }}
          >
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={onClickRemove}
            style={{ borderRadius: '8px', fontWeight: '600', background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', border: 'none' }}
          >
            Yes, Logout
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

Header.propTypes = {
  title: PropTypes.string,
};

export default Header;
